import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Users, Save, RotateCcw, GripVertical, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { nbaToday, shortDate } from "@/lib/format";
import "@/pages/Rotations.css";

const TIERS = [
  { key: "starters", tier: "starter", label: "Starters", cls: "t-starter", mk: "starter" },
  { key: "rotation", tier: "rotation", label: "Rotation", cls: "t-rotation", mk: "rotation" },
  { key: "garbage", tier: "garbage", label: "Garbage Time", cls: "t-garbage", mk: "garbage" },
];
const shift = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// ---- shared player row ----
function PlayerRow({ p, tierKey, dirty, editable, locked, onLock, onDragStart }) {
  const pr = p.proj || {}; const r = p.rates || {};
  return (
    <tr className={`${tierKey === "garbage" ? "garb" : ""} ${p.out ? "is-out" : ""}`}
        draggable={editable} onDragStart={editable ? onDragStart : undefined}
        data-testid={`rot-player-${p.player_id}`}>
      <td className="l">
        <div className="rot-pname">
          {editable && <GripVertical size={14} className="rot-grip" />}
          {p.photo ? <img src={p.photo} alt="" referrerPolicy="no-referrer" /> : <span style={{ width: 30 }} />}
          <div>
            {p.name}
            {p.out && <span className="rot-out">OUT</span>}
            {p.overridden && <span className="rot-moved">moved</span>}
            <span className="rot-prate">{(r.pts ?? 0).toFixed(2)} pts/min · {Math.round((p.availability || 0) * 100)}% avail</span>
          </div>
        </div>
      </td>
      <td>{p.starts}</td>
      <td>{p.weighted_mpg}</td>
      {editable && tierKey !== "garbage" && (
        <td>
          <input className="rot-lock-input" type="number" min="0" max="44" placeholder="auto"
                 value={locked ?? ""} onChange={(e) => onLock(p.player_id, e.target.value)}
                 data-testid={`rot-lock-${p.player_id}`} />
          {locked != null && <Lock size={11} className="rot-lock-ic" />}
        </td>
      )}
      <td className="rot-min">{dirty ? "—" : p.proj_min}</td>
      <td>{dirty ? "—" : (pr.pts ?? "—")}</td>
      <td>{dirty ? "—" : (pr.reb ?? "—")}</td>
      <td>{dirty ? "—" : (pr.ast ?? "—")}</td>
      <td>{dirty ? "—" : (pr.fg3m ?? "—")}</td>
    </tr>
  );
}

function TierHead({ label, cls, count, hist, proj, editable }) {
  return (
    <div className="rot-tier-head">
      <h2 className={cls}>{label}</h2>
      <small>
        {count} players
        {hist != null && <> · Φέτος {hist}′</>}
        {proj != null && <> · Proj {proj}′</>}
      </small>
    </div>
  );
}

// ===================== TEAM (season) MODE — drag & drop editor =====================
function TeamMode({ sel, onSelectTeam, teams }) {
  const [data, setData] = useState(null);
  const [lists, setLists] = useState(null);
  const [locks, setLocks] = useState({});   // pid -> minutes
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragId = useRef(null);

  const hydrate = useCallback((d) => {
    setData(d);
    setLists({ starters: [...d.starters], rotation: [...d.rotation], garbage: [...d.garbage] });
    const lk = {};
    [...d.starters, ...d.rotation].forEach((p) => { if (p.locked) lk[p.player_id] = p.proj_min; });
    setLocks(lk);
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!sel) return;
    setLoading(true); setData(null); setLists(null);
    api.get(`/team-rotation/${sel}`).then((r) => hydrate(r.data)).catch((e) => toast.error(errorText(e))).finally(() => setLoading(false));
  }, [sel, hydrate]);

  const moveTo = (tierKey) => {
    const pid = dragId.current; dragId.current = null;
    if (!pid || !lists) return;
    const from = TIERS.find((t) => lists[t.key].some((p) => p.player_id === pid));
    if (!from || from.key === tierKey) return;
    const player = lists[from.key].find((p) => p.player_id === pid);
    const rankDesc = (a, b) => (b.starts - a.starts) || (b.weighted_mpg - a.weighted_mpg);
    setLists((prev) => {
      const next = { ...prev, [from.key]: prev[from.key].filter((p) => p.player_id !== pid), [tierKey]: [...prev[tierKey], player] };
      // Invariant: Starters are ALWAYS exactly 5. Dragging one in bumps the weakest other starter to Rotation;
      // dragging one out promotes the strongest Rotation player up.
      if (tierKey === "starters" && next.starters.length > 5) {
        const demote = [...next.starters].filter((p) => p.player_id !== pid).sort(rankDesc).pop();
        if (demote) {
          next.starters = next.starters.filter((p) => p.player_id !== demote.player_id);
          next.rotation = [...next.rotation, demote];
          toast.info(`${demote.name} → Rotation (οι starters μένουν 5)`);
        }
      } else if (from.key === "starters" && next.starters.length < 5) {
        const promote = [...next.rotation].filter((p) => p.player_id !== pid).sort(rankDesc)[0];
        if (promote) {
          next.rotation = next.rotation.filter((p) => p.player_id !== promote.player_id);
          next.starters = [...next.starters, promote];
          toast.info(`${promote.name} → Starters (οι starters μένουν 5)`);
        }
      }
      return next;
    });
    if (tierKey === "garbage" && locks[pid] != null) setLocks((l) => { const n = { ...l }; delete n[pid]; return n; });
    setDirty(true);
  };

  const onLock = (pid, val) => {
    setLocks((l) => { const n = { ...l }; if (val === "" || val == null) delete n[pid]; else n[pid] = Math.max(0, Math.min(44, Number(val))); return n; });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    const tiers = {};
    TIERS.forEach(({ key, tier }) => lists[key].forEach((p) => { tiers[p.player_id] = tier; }));
    try { const r = await api.post(`/team-rotation/${sel}`, { tiers, locked: locks }); hydrate(r.data); toast.success("Αποθηκεύτηκε — επανυπολογισμός ολοκληρώθηκε"); }
    catch (e) { toast.error(errorText(e)); }
    setSaving(false);
  };
  const reset = async () => {
    setSaving(true);
    try { const r = await api.delete(`/team-rotation/${sel}/override`); hydrate(r.data); toast.success("Επαναφορά στην αυτόματη κατηγοριοποίηση"); }
    catch (e) { toast.error(errorText(e)); }
    setSaving(false);
  };

  const chips = useMemo(() => {
    if (!data) return [];
    const h = data.tier_minutes_hist || {};
    return [
      ["Season", data.season_label],
      ["Team games", data.team_games],
      ["Starters", `${Math.round(h.starter ?? 0)}′/game`],
      ["Bench", `${Math.round(h.rotation ?? 0)}′/game`],
      ["Players used / game", data.avg_players_used],
      ["Rotation size", dirty ? "—" : data.rotation_size],
    ];
  }, [data, dirty]);

  return (
    <>
      <div className="rot-bar">
        <select className="rot-select" value={sel} onChange={(e) => onSelectTeam(e.target.value)} data-testid="rot-team-select">
          {teams.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
        </select>
        <div className="rot-chips">{chips.map(([k, v]) => <span key={k} className="rot-chip">{k} <b>{v}</b></span>)}</div>
        <div className="rot-actions">
          <button type="button" className="rot-btn ghost" onClick={reset} disabled={saving || !data?.has_override} data-testid="rot-reset-btn"><RotateCcw size={15} /> Reset</button>
          <button type="button" className="rot-btn primary" onClick={save} disabled={saving || !dirty} data-testid="rot-save-btn"><Save size={15} /> {saving ? "…" : "Save"}</button>
        </div>
      </div>
      {data?.has_override && <span className="rot-manual" data-testid="rot-manual-badge">MANUAL</span>}
      {dirty && <div className="rot-dirty" data-testid="rot-dirty-banner">Μη αποθηκευμένες αλλαγές — πάτησε Save για να υπολογιστούν ξανά λεπτά & στατιστικά.</div>}

      {loading ? <p className="rot-dim" data-testid="rot-loading">Υπολογισμός…</p> : !lists ? null : (
        <>
          {TIERS.map(({ key, label, cls }) => (
            <section className="rot-tier" key={key} data-testid={`rot-tier-${key}`} onDragOver={(e) => e.preventDefault()} onDrop={() => moveTo(key)}>
              <TierHead label={label} cls={cls} count={lists[key].length} hist={data.tier_minutes_hist?.[key === "starters" ? "starter" : key === "rotation" ? "rotation" : "garbage"]} proj={dirty ? null : data.tier_minutes_proj?.[key === "starters" ? "starter" : key === "rotation" ? "rotation" : "garbage"]} editable />
              {!lists[key].length ? <p className="rot-dim">Σύρε παίκτες εδώ</p> : (
                <table className="rot-table">
                  <thead><tr><th className="l">Player</th><th>Starts</th><th>wMPG</th>{key !== "garbage" && <th>Lock′</th>}<th>Proj MIN</th><th>PTS</th><th>REB</th><th>AST</th><th>3PM</th></tr></thead>
                  <tbody>{lists[key].map((p) => (
                    <PlayerRow key={p.player_id} p={p} tierKey={key} dirty={dirty} editable locked={locks[p.player_id]}
                               onLock={onLock} onDragStart={() => { dragId.current = p.player_id; }} />
                  ))}</tbody>
                </table>
              )}
            </section>
          ))}
          {data?.team_totals && (
            <section className="rot-tier" data-testid="rot-team-totals">
              <table className="rot-table"><tbody><tr className="rot-totals">
                <td className="l">PROJECTED TEAM TOTAL</td><td colSpan={3} />
                <td className="rot-min">{dirty ? "—" : data.allocated_minutes}</td>
                <td data-testid="rot-total-pts">{dirty ? "—" : data.team_totals.pts}</td>
                <td>{dirty ? "—" : data.team_totals.reb}</td><td>{dirty ? "—" : data.team_totals.ast}</td><td>{dirty ? "—" : data.team_totals.fg3m}</td>
              </tr></tbody></table>
            </section>
          )}
        </>
      )}
    </>
  );
}

// ===================== GAME MODE — opponent band + home + OUT + B2B =====================
function GameTeam({ t }) {
  return (
    <div className="rot-gt" data-testid={`rot-gt-${t.team.code}`}>
      <div className="rot-gt-head">
        <span className="rot-gt-code">{t.team.code}</span>
        <span className={`rot-gt-badge ${t.home ? "home" : "away"}`}>{t.home ? "HOME" : "AWAY"}</span>
        {t.opp_band && <span className="rot-gt-band" title={`Band ${t.opp_band}`}>{t.opp_band_label}</span>}
        {t.b2b && <span className="rot-gt-b2b">B2B −10%</span>}
        <span className="rot-gt-pts" data-testid={`rot-gt-pts-${t.team.code}`}>{Math.round(t.team_totals.pts)}</span>
      </div>
      {TIERS.map(({ key, label, cls }) => t[key].length ? (
        <section className="rot-tier" key={key}>
          <TierHead label={label} cls={cls} count={t[key].length} proj={t.tier_minutes_proj?.[key === "starters" ? "starter" : key === "rotation" ? "rotation" : "garbage"]} />
          <table className="rot-table">
            <thead><tr><th className="l">Player</th><th>Starts</th><th>wMPG</th><th>Proj MIN</th><th>PTS</th><th>REB</th><th>AST</th><th>3PM</th></tr></thead>
            <tbody>{t[key].map((p) => <PlayerRow key={p.player_id} p={p} tierKey={key} dirty={false} editable={false} />)}</tbody>
          </table>
        </section>
      ) : null)}
    </div>
  );
}

// Edge Highlight: colour the model−market difference by magnitude (>= EDGE_THRESHOLD = green edge, else red).
const EDGE_THRESHOLD = 4;
function EdgeCell({ label, diff }) {
  if (diff == null) return null;
  const edge = Math.abs(diff) >= EDGE_THRESHOLD;
  return (
    <span className={`rot-edge ${edge ? "edge-green" : "edge-red"}`} data-testid={`rot-edge-${label.toLowerCase()}`}>
      {label} {diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1)}
    </span>
  );
}
function EdgeBadges({ model, market }) {
  if (!market) return null;
  const spreadDiff = market.spread_home != null ? model.spread_home - market.spread_home : null;
  const totalDiff = market.total != null ? model.total - market.total : null;
  if (spreadDiff == null && totalDiff == null) return null;
  return (
    <div className="rot-edges" data-testid="rot-edges" title={`Διαφορά Μοντέλου−Αγοράς · πράσινο ≥ ${EDGE_THRESHOLD}`}>
      <span className="rot-edge-label">Edge</span>
      <EdgeCell label="Spread" diff={spreadDiff} />
      <EdgeCell label="Total" diff={totalDiff} />
    </div>
  );
}

function GameMode() {
  const [day, setDay] = useState(nbaToday());
  const [games, setGames] = useState(null);
  const [sel, setSel] = useState("");
  const [b2b, setB2b] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setGames(null); setData(null);
    api.get("/season-model/games", { params: { day } }).then((r) => { setGames(r.data); setSel(r.data[0]?.id || ""); }).catch((e) => toast.error(errorText(e)));
  }, [day]);

  useEffect(() => {
    if (!sel) { setData(null); return; }
    setLoading(true); setData(null);
    api.get(`/rotation-projection/${sel}`, { params: { b2b_adjust: b2b } }).then((r) => setData(r.data)).catch((e) => toast.error(errorText(e))).finally(() => setLoading(false));
  }, [sel, b2b]);

  return (
    <>
      <div className="rot-bar">
        <div className="rot-day">
          <button type="button" onClick={() => setDay(shift(day, -1))} aria-label="Previous day" data-testid="rot-prev-day"><ChevronLeft size={16} /></button>
          <span data-testid="rot-day">{shortDate(day)}</span>
          <button type="button" onClick={() => setDay(shift(day, 1))} aria-label="Next day" data-testid="rot-next-day"><ChevronRight size={16} /></button>
        </div>
        <label className="rot-b2b-toggle" data-testid="rot-b2b-toggle">
          <input type="checkbox" checked={b2b} onChange={(e) => setB2b(e.target.checked)} /> Back-to-back −10%
        </label>
      </div>
      {!games ? <p className="rot-dim">Φόρτωση…</p> : !games.length ? <p className="rot-dim" data-testid="rot-no-games">Δεν υπάρχουν παιχνίδια.</p> : (
        <>
          <div className="rot-games" data-testid="rot-games">
            {games.map((g) => (
              <button key={g.id} type="button" className={sel === g.id ? "is-active" : ""} onClick={() => setSel(g.id)} data-testid={`rot-game-${g.id}`}>
                {g.away_code} @ {g.home_code}
              </button>
            ))}
          </div>
          {loading ? <p className="rot-dim">Υπολογισμός…</p> : !data ? null : (
            <>
              <div className="rot-score" data-testid="rot-proj-score">
                {Object.entries(data.projected_score).map(([code, pts], i) => (
                  <span key={code}>{i > 0 && <em>—</em>}{code} <b>{pts}</b></span>
                ))}
              </div>
              <div className="rot-lines" data-testid="rot-lines">
                <div className="rot-line model">
                  <span className="rot-line-tag">Μοντέλο</span>
                  Spread {data.model_line.spread_home > 0 ? `+${data.model_line.spread_home}` : data.model_line.spread_home} · Total {data.model_line.total}
                </div>
                <div className="rot-line market">
                  <span className="rot-line-tag">{data.market_line?.provider || "DraftKings"}</span>
                  {data.market_line && (data.market_line.spread_home != null || data.market_line.total != null) ? (
                    <>Spread {data.market_line.spread_home ?? "—"} · Total {data.market_line.total ?? "—"}</>
                  ) : <span className="rot-line-none">χωρίς γραμμή</span>}
                </div>
                <EdgeBadges model={data.model_line} market={data.market_line} />
              </div>
              <div className="rot-gcols">{data.teams.map((t) => <GameTeam key={t.team.code} t={t} />)}</div>
            </>
          )}
        </>
      )}
    </>
  );
}

export default function Rotations() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState("team");
  const [teams, setTeams] = useState([]);
  const [sel, setSel] = useState(params.get("team") || "");

  useEffect(() => {
    api.get("/teams").then((r) => {
      const sorted = [...r.data].sort((a, b) => a.name.localeCompare(b.name));
      setTeams(sorted); setSel((cur) => cur || sorted[0]?.id || "");
    }).catch((e) => toast.error(errorText(e)));
  }, []);

  const onSelectTeam = (id) => { setSel(id); setParams(id ? { team: id } : {}); };

  return (
    <div className="rot-root" data-testid="rotations-page">
      <header className="rot-head">
        <span className="rot-icon"><Users size={24} /></span>
        <div>
          <h1>TEAM <span>ROTATIONS</span></h1>
          <p>Ομάδα: σύρε παίκτη σε άλλη κατηγορία & κλείδωσε λεπτά, μετά Save. Παιχνίδι: αντίπαλος (odds), έδρα, OUT & back-to-back. Τα 240 λεπτά γεμίζουν μόνο από Starters + Rotation.</p>
        </div>
      </header>
      <div className="rot-modes">
        <button type="button" className={mode === "team" ? "is-active" : ""} onClick={() => setMode("team")} data-testid="rot-mode-team">Ομάδα</button>
        <button type="button" className={mode === "game" ? "is-active" : ""} onClick={() => setMode("game")} data-testid="rot-mode-game">Παιχνίδι</button>
      </div>
      {mode === "team" ? <TeamMode sel={sel} onSelectTeam={onSelectTeam} teams={teams} /> : <GameMode />}
    </div>
  );
}
